import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MessageCircle, Loader2, Check, ChevronDown, ChevronUp, Send, Mail, MessageSquare, Sparkles, Trash2, Bot, Phone } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const SMS_SEGMENT_LIMIT = 160;

// Voicemail left via the phone receptionist — streamed through our API (Twilio
// media needs account auth) so the advisor can just press play.
function VoicemailPlayer({ sid, duration }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/twilio/voice/recording/${sid}`, { responseType: "blob" });
      setSrc(URL.createObjectURL(r.data));
    } catch { toast.error("Couldn't load the recording"); }
    finally { setLoading(false); }
  };
  if (src) return <audio src={src} controls autoPlay className="mt-2 w-full h-9" data-testid={`voicemail-audio-${sid}`} />;
  return (
    <button type="button" onClick={load} disabled={loading} className="btn-outline text-xs mt-2 inline-flex items-center gap-1" data-testid={`voicemail-play-${sid}`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />} Play voicemail{duration ? ` · ${duration}s` : ""}
    </button>
  );
}

export default function CustomerMessagesPanel({ jobId }) {
  const { user } = useAuth();
  const canDelete = !!(user?.platform_owner ||
    user?.role_in_workshop === "owner" ||
    user?.role_in_workshop === "manager");
  const [data, setData] = useState({
    messages: [], unread_count: 0,
    can_reply_sms: false, can_reply_email: false,
    customer_phone: "", customer_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyChannel, setReplyChannel] = useState(null); // 'sms' | 'email' | null (auto)
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingAi, setTogglingAi] = useState(false);
  const [originalDraft, setOriginalDraft] = useState(""); // set when Suggest populates; used to log advisor edits for house-tone learning
  const [toneStatus, setToneStatus] = useState({ examples_count: 0, guide_ready: false });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/jobs/${jobId}/messages`);
      setData(r.data);
    } catch { /* ignored */ }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    refresh();
    const t = setInterval(refresh, 30_000); // poll every 30s for new customer pings
    return () => clearInterval(t);
  }, [jobId, refresh]);

  // Fetch AI house-tone learning status (shown as caption under Suggest)
  useEffect(() => {
    let cancelled = false;
    api.get("/ai-tone/status")
      .then((r) => { if (!cancelled) setToneStatus(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jobId]);

  // Auto-choose reply channel: last inbound → same channel. Else prefer SMS, then email.
  const suggestedChannel = useMemo(() => {
    const lastInbound = data.messages.find((m) => (m.from || "customer") === "customer");
    if (lastInbound?.channel === "email" && data.can_reply_email) return "email";
    if (lastInbound?.channel === "sms" && data.can_reply_sms) return "sms";
    if (data.can_reply_sms) return "sms";
    if (data.can_reply_email) return "email";
    return null;
  }, [data]);
  const activeChannel = replyChannel || suggestedChannel;

  const markRead = async (msgId) => {
    try {
      await api.post(`/jobs/${jobId}/messages/${msgId}/read`, {});
      refresh();
    } catch { toast.error("Couldn't mark as read"); }
  };

  const deleteMessage = async (msgId, preview) => {
    if (!window.confirm(`Delete this message permanently?\n\n"${(preview || "").slice(0, 120)}"`)) return;
    setDeletingId(msgId);
    try {
      await api.delete(`/jobs/${jobId}/messages/${msgId}`);
      toast.success("Message deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete message");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleAiThread = async () => {
    const paused = data.ai_advisor?.paused;
    setTogglingAi(true);
    try {
      await api.post(`/jobs/${jobId}/ai-advisor/${paused ? "resume" : "pause"}`, {});
      toast.success(paused ? "AI assistant resumed on this customer" : "You've taken over — AI is paused for this customer");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the assistant");
    } finally { setTogglingAi(false); }
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || !activeChannel) return;
    setSending(true);
    try {
      const payload = { text, channel: activeChannel };
      // If the advisor started from an AI draft and edited it, ship the
      // original for the invisible house-tone-learning loop.
      if (originalDraft && originalDraft !== text) {
        payload.original_ai_draft = originalDraft;
      }
      const r = await api.post(`/jobs/${jobId}/messages/reply`, payload);
      if (r?.data?.delivery === "failed") {
        toast.warning("Saved to the thread, but it didn't reach them", {
          description: r.data.delivery_error || "Check the customer's contact details, or give them a call.",
        });
      } else {
        toast.success(`Reply sent via ${activeChannel.toUpperCase()}`);
      }
      setReplyText("");
      setOriginalDraft("");
      refresh();
      // Refresh tone status — the count of learned examples may have ticked up
      api.get("/ai-tone/status").then((r) => setToneStatus(r.data || {})).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reply");
    } finally { setSending(false); }
  };

  const draftWithAi = async () => {
    if (!activeChannel) return;
    setDrafting(true);
    try {
      const r = await api.post(`/jobs/${jobId}/messages/ai-draft-reply`, { channel: activeChannel });
      const draft = r?.data?.draft || "";
      if (!draft) throw new Error("Empty draft");
      setReplyText(draft);
      setOriginalDraft(draft);
      toast.success("Draft ready — review, tweak, then send");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const hasInboundToDraftFrom = useMemo(
    () => data.messages.some((m) => (m.from || "customer") === "customer"),
    [data.messages]
  );

  if (loading) return null;

  const showEmpty = data.messages.length === 0;
  const canReply = data.can_reply_sms || data.can_reply_email;
  const segments = activeChannel === "sms"
    ? Math.max(1, Math.ceil(replyText.length / SMS_SEGMENT_LIMIT))
    : 0;

  return (
    <div
      className={`card-default ${data.unread_count > 0 ? "border-primary/50 bg-primary/5" : "border-border"}`}
      data-testid="customer-messages-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors text-left"
        data-testid="customer-messages-toggle"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center ${data.unread_count > 0 ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground"}`}>
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <div className="label-overline">Customer messages</div>
            <div className="font-display font-bold text-lg mt-0.5">
              {data.unread_count > 0
                ? <>{data.unread_count} new from your customer</>
                : showEmpty
                  ? <>Send a message</>
                  : <>{data.messages.length} message{data.messages.length === 1 ? "" : "s"}</>}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-2">
          {data.ai_advisor?.workshop_enabled && (
            <div
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-xs ${data.ai_advisor.paused ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-primary/30 bg-primary/[0.05]"}`}
              data-testid="ai-advisor-thread-strip"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Bot className={`w-4 h-4 shrink-0 ${data.ai_advisor.paused ? "text-amber-300" : "text-primary"}`} />
                <span className="truncate">
                  {data.ai_advisor.paused
                    ? <><strong>You&apos;re handling this customer</strong>{data.ai_advisor.handoff?.reason ? <> · handed over: {data.ai_advisor.handoff.reason}</> : null}</>
                    : <><strong>{data.ai_advisor.assistant_name}</strong> is answering this customer&apos;s questions</>}
                </span>
              </div>
              <button
                type="button"
                onClick={toggleAiThread}
                disabled={togglingAi}
                className="btn-outline text-[11px] shrink-0"
                data-testid="ai-advisor-thread-toggle"
              >
                {togglingAi ? <Loader2 className="w-3 h-3 animate-spin" /> : data.ai_advisor.paused ? "Resume AI" : "Take over"}
              </button>
            </div>
          )}
          {data.messages.map((m) => {
            const isOutbound = (m.from || "customer") === "workshop";
            return (
              <div
                key={m.id}
                className={
                  isOutbound
                    ? "p-4 rounded-lg border border-border/60 bg-muted/20 ml-8"
                    : `p-4 rounded-lg border ${!m.read_at ? "border-primary/40 bg-primary/5" : "border-border"} mr-8`
                }
                data-testid={`customer-message-${m.id}`}
              >
                {isOutbound && (
                  <div className="label-overline text-[9px] text-muted-foreground mb-1 flex items-center gap-1">
                    {m.ai ? (
                      <span className="inline-flex items-center gap-1 text-primary" data-testid={`customer-message-ai-${m.id}`}>
                        <Bot className="w-2.5 h-2.5" /> AI assistant replied
                        {m.ai_handoff && <span className="text-amber-300">· handed over to you</span>}
                      </span>
                    ) : (
                      <><Send className="w-2.5 h-2.5" /> You replied</>
                    )}
                    {m.sent_by_name && !m.ai && <span className="text-muted-foreground/70">· {m.sent_by_name}</span>}
                    {m.delivery === "failed" && <span className="text-amber-300">· didn&apos;t reach them</span>}
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{isOutbound ? m.text : <>&ldquo;{m.text}&rdquo;</>}</div>
                {m.channel === "voice" && m.recording_sid && (
                  <VoicemailPlayer sid={m.recording_sid} duration={m.duration_s} />
                )}
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-plex text-muted-foreground">{timeAgo(m.created_at)}</span>
                    {m.channel === "sms" && (
                      <span
                        className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30"
                        title={m.from_phone ? `via SMS from ${m.from_phone}` : m.to_phone ? `via SMS to ${m.to_phone}` : "SMS"}
                        data-testid={`customer-message-channel-sms-${m.id}`}
                      >
                        SMS
                      </span>
                    )}
                    {m.channel === "email" && (
                      <span
                        className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-success/15 text-success border border-success/30"
                        title={m.from_email ? `via Email from ${m.from_email}` : m.to_email ? `via Email to ${m.to_email}` : "Email"}
                        data-testid={`customer-message-channel-email-${m.id}`}
                      >
                        Email
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {!isOutbound && (
                      !m.read_at ? (
                        <button
                          type="button"
                          onClick={() => markRead(m.id)}
                          className="text-primary hover:underline inline-flex items-center gap-1 font-semibold"
                          data-testid={`customer-message-read-${m.id}`}
                        >
                          <Check className="w-3 h-3" /> Mark as read
                        </button>
                      ) : (
                        <span className="text-muted-foreground inline-flex items-center gap-1">
                          <Check className="w-3 h-3 text-success" /> Read
                        </span>
                      )
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => deleteMessage(m.id, m.text)}
                        disabled={deletingId === m.id}
                        className="text-muted-foreground hover:text-destructive inline-flex items-center gap-1 transition-colors disabled:opacity-40"
                        title="Delete this message permanently (owner-only)"
                        data-testid={`customer-message-delete-${m.id}`}
                      >
                        {deletingId === m.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {data.unread_count > 0 && (
            <div className="text-[11px] text-muted-foreground text-center pt-1">
              <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Polling for new messages every 30s
            </div>
          )}

          {/* Reply composer */}
          {canReply ? (
            <div className="mt-4 pt-4 border-t border-border" data-testid="customer-reply-composer">
              <div className="flex items-center gap-2 mb-2">
                <span className="label-overline text-[9px]">Reply via</span>
                {data.can_reply_sms && (
                  <button
                    type="button"
                    onClick={() => setReplyChannel("sms")}
                    className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors ${activeChannel === "sms" ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:bg-muted/30"}`}
                    data-testid="reply-channel-sms"
                  >
                    <MessageSquare className="w-3 h-3" /> SMS
                  </button>
                )}
                {data.can_reply_email && (
                  <button
                    type="button"
                    onClick={() => setReplyChannel("email")}
                    className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors ${activeChannel === "email" ? "bg-success text-success-foreground border-success" : "bg-transparent text-muted-foreground border-border hover:bg-muted/30"}`}
                    data-testid="reply-channel-email"
                  >
                    <Mail className="w-3 h-3" /> Email
                  </button>
                )}
                <span className="text-[10px] text-muted-foreground ml-auto font-mono-plex">
                  {activeChannel === "sms" && data.customer_phone}
                  {activeChannel === "email" && data.customer_email}
                </span>
              </div>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReply(); }
                }}
                placeholder={activeChannel === "sms" ? "Type your SMS reply… (Cmd+Enter to send)" : "Type your email reply… (Cmd+Enter to send)"}
                rows={3}
                className="w-full text-sm p-3 rounded-md border border-border bg-background focus:outline-none focus:border-primary transition-colors resize-y"
                data-testid="reply-textarea"
                disabled={sending}
                maxLength={2000}
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground font-mono-plex">
                  {activeChannel === "sms"
                    ? <>{replyText.length}/{SMS_SEGMENT_LIMIT * 2} chars · {segments} segment{segments === 1 ? "" : "s"}</>
                    : <>{replyText.length}/2000 chars</>}
                </span>
                <div className="flex items-center gap-2">
                  {hasInboundToDraftFrom && (
                    <button
                      type="button"
                      onClick={draftWithAi}
                      disabled={drafting || sending || !activeChannel}
                      className="btn-outline text-xs inline-flex items-center gap-1"
                      title="Let Claude draft a reply based on the customer's message + job context"
                      data-testid="reply-ai-draft-btn"
                    >
                      {drafting
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Drafting…</>
                        : <><Sparkles className="w-3 h-3" /> Suggest</>}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={!replyText.trim() || !activeChannel || sending}
                    className="btn-primary text-xs inline-flex items-center gap-1"
                    data-testid="reply-send-btn"
                  >
                    {sending ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : <><Send className="w-3 h-3" /> Send</>}
                  </button>
                </div>
              </div>
              {hasInboundToDraftFrom && toneStatus.examples_count > 0 && (
                <div
                  className="mt-2 text-[10px] text-muted-foreground text-right font-mono-plex"
                  title="Every time you tweak an AI draft before sending, we learn your workshop's house tone"
                  data-testid="ai-tone-caption"
                >
                  {toneStatus.guide_ready
                    ? <>🎓 AI is sounding like your team · learned from {toneStatus.examples_count} of your replies</>
                    : <>🎓 Learning your house tone · {toneStatus.examples_count}/3 replies captured</>}
                </div>
              )}
            </div>
          ) : (
            showEmpty && (
              <div className="text-[11px] text-muted-foreground text-center py-6">
                Add a phone or email on the customer record to enable replies.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
